import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useMatterStore } from './matterStore';
import { captureEvent } from '../analytics';

/**
 * Dialog session returned by the `/api/Matter/{pid}/DialogSession` endpoints.
 * Only the fields the frontend actually reads are listed — extend as consumers grow.
 */
export interface DialogSession {
    RecordId: string;
    Name: string;
    CreatedTimestamp?: string;
    LastInteractionTimestamp?: string;
}

export const useSessionStore = defineStore('session', () => {
    const sessions = ref<DialogSession[]>([]);
    const currentSession = ref<DialogSession | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    // Matter id the `sessions` list currently reflects. Lets us skip the
    // /DialogSession fetch when the sidebar is collapsed and re-expanded
    // (which unmounts/remounts the list).
    const loadedMatterId = ref<string | null>(null);

    // Per-matter cache of session lists, keyed by matter RecordId. Persists
    // across matter switches for the lifetime of the app session, so
    // revisiting a matter swaps in its list instantly instead of refetching.
    // The cached array is the same reference held by `sessions` while that
    // matter is active, so in-place mutations (create/rename) stay in sync;
    // operations that reassign `sessions` (delete) write the new array back.
    // Not reactive on purpose — only the active `sessions` ref drives the UI.
    const sessionCache = new Map<string, DialogSession[]>();
    const isDataFrameViewerVisible = ref(false);
    const selectedChatIdForDataframe = ref<string | null>(null);
    const selectedChatPreviewText = ref('');

    const hasSessions = computed(() => sessions.value.length > 0);
    const isSessionSelected = computed(() => currentSession.value !== null);
    const currentSessionId = computed(() => currentSession.value?.RecordId || null);

    /**
     * Fetch the matter's session list. Cached per matter for the app session:
     * a no-op when that matter's list is already active, and an instant swap
     * (no network) when the matter was visited earlier — so re-mounting the
     * sidebar (collapse/expand) or switching back to a matter doesn't re-hit
     * the endpoint. Pass `force` to bypass the cache and refetch.
     */
    async function loadSessions(force = false) {
        const matterStore = useMatterStore();
        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }

        const matterId = matterStore.currentMatter.RecordId;
        if (!force && loadedMatterId.value === matterId) {
            return;
        }
        if (!force && sessionCache.has(matterId)) {
            sessions.value = sessionCache.get(matterId)!;
            loadedMatterId.value = matterId;
            return;
        }

        try {
            loading.value = true;
            error.value = null;
            const response = await fetch(`/api/Matter/${matterId}/DialogSession`);
            if (!response.ok) {
                throw new Error('Failed to load sessions');
            }
            const data: DialogSession[] = await response.json();
            sessionCache.set(matterId, data);
            sessions.value = data;
            loadedMatterId.value = matterId;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    async function createSession(name: string) {
        const matterStore = useMatterStore();
        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }

        try {
            const response = await fetch(
                `/api/Matter/${matterStore.currentMatter.RecordId}/DialogSession`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ Name: name }),
                },
            );

            if (!response.ok) {
                throw new Error('Failed to create session');
            }

            const newSession: DialogSession = await response.json();
            sessions.value.push(newSession);
            captureEvent('session_created', {
                matter_id: matterStore.currentMatter.RecordId,
                session_id: newSession.RecordId,
            });
            return newSession;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        }
    }

    function selectSession(session: DialogSession) {
        currentSession.value = session;
    }

    /**
     * The session with the most recent activity, or null if the matter has
     * none. Used to land the user on their latest dialogue when they open a
     * matter rather than dumping them on the empty "New chat" state. Ranks by
     * LastInteractionTimestamp, falling back to CreatedTimestamp; sessions
     * with neither sort last.
     */
    const mostRecentSession = computed<DialogSession | null>(() => {
        const ts = (s: DialogSession) =>
            s.LastInteractionTimestamp
                ? new Date(s.LastInteractionTimestamp).getTime()
                : s.CreatedTimestamp
                  ? new Date(s.CreatedTimestamp).getTime()
                  : 0;
        let best: DialogSession | null = null;
        let bestTs = -Infinity;
        for (const session of sessions.value) {
            const t = ts(session);
            if (t > bestTs) {
                bestTs = t;
                best = session;
            }
        }
        return best;
    });

    function clearCurrentSession() {
        currentSession.value = null;
    }

    // Called on matter change (from App.vue). Nulls currentSession so the
    // chat-clear cascade fires, and swaps the visible `sessions` list to the
    // new matter's cached entry when it was visited earlier (instant, no
    // refetch) or an empty list otherwise — leaving the per-matter cache
    // intact so revisits stay cheap.
    function clearAllSessions() {
        currentSession.value = null;
        const matterStore = useMatterStore();
        const matterId = matterStore.currentMatter?.RecordId ?? null;
        if (matterId && sessionCache.has(matterId)) {
            sessions.value = sessionCache.get(matterId)!;
            loadedMatterId.value = matterId;
        } else {
            sessions.value = [];
            loadedMatterId.value = null;
        }
    }

    /**
     * Hydrate `currentSession` from a session id (used by the router when
     * a deep link arrives cold). Loads the session list if empty, then
     * locates the requested id. Returns false if the id isn't known so
     * the caller can redirect; throws if the list fetch itself fails.
     * No-ops when the requested id is already selected.
     */
    async function ensureSessionLoaded(sessionId: string): Promise<boolean> {
        if (currentSession.value?.RecordId === sessionId) return true;
        if (sessions.value.length === 0) {
            await loadSessions();
        }
        const found = sessions.value.find((s) => s.RecordId === sessionId);
        if (!found) return false;
        currentSession.value = found;
        return true;
    }

    async function deleteSession(sessionId: string) {
        const matterStore = useMatterStore();
        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }

        try {
            const response = await fetch(
                `/api/Matter/${matterStore.currentMatter.RecordId}/DialogSession/${sessionId}`,
                {
                    method: 'DELETE',
                },
            );

            if (!response.ok) {
                throw new Error('Failed to delete session');
            }

            sessions.value = sessions.value.filter((s) => s.RecordId !== sessionId);
            // filter() reassigns the array, breaking the shared reference with
            // the cache entry — write the new array back so the cache matches.
            sessionCache.set(matterStore.currentMatter.RecordId, sessions.value);

            if (currentSession.value?.RecordId === sessionId) {
                currentSession.value = null;
            }

            captureEvent('session_deleted', {
                matter_id: matterStore.currentMatter.RecordId,
                session_id: sessionId,
            });
            return true;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        }
    }

    async function renameSession(sessionId: string, newName: string) {
        const matterStore = useMatterStore();
        if (!matterStore.currentMatter) {
            throw new Error('No matter selected');
        }

        try {
            const response = await fetch(
                `/api/Matter/${matterStore.currentMatter.RecordId}/DialogSession/${sessionId}/Rename`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ NewName: newName }),
                },
            );

            if (!response.ok) {
                throw new Error('Failed to rename session');
            }

            const updatedSession: DialogSession = await response.json();

            const index = sessions.value.findIndex((s) => s.RecordId === sessionId);
            if (index !== -1) {
                sessions.value[index] = updatedSession;
            }

            if (currentSession.value?.RecordId === sessionId) {
                currentSession.value = updatedSession;
            }

            captureEvent('session_renamed', {
                matter_id: matterStore.currentMatter.RecordId,
                session_id: sessionId,
            });
            return updatedSession;
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        }
    }

    function showDataFrameViewer(chatId: string, chatPreviewText: string) {
        selectedChatIdForDataframe.value = chatId;
        selectedChatPreviewText.value = chatPreviewText;
        isDataFrameViewerVisible.value = true;
    }

    function hideDataFrameViewer() {
        isDataFrameViewerVisible.value = false;
        selectedChatIdForDataframe.value = null;
        selectedChatPreviewText.value = '';
    }

    return {
        sessions,
        currentSession,
        loading,
        error,
        isDataFrameViewerVisible,
        selectedChatIdForDataframe,
        selectedChatPreviewText,
        hasSessions,
        isSessionSelected,
        currentSessionId,
        mostRecentSession,
        loadSessions,
        createSession,
        selectSession,
        clearCurrentSession,
        clearAllSessions,
        ensureSessionLoaded,
        deleteSession,
        renameSession,
        showDataFrameViewer,
        hideDataFrameViewer,
    };
});
